/**
 * JSON-RPC MCP tool client for app-scoped dc__* tools.
 * POST /mcp/app/:app_id with Bearer MCP key (not operator JWT).
 */

export interface McpToolCallResult<T = unknown> {
  raw: unknown;
  data: T;
}

function parseToolContentText(result: unknown): unknown {
  if (!result || typeof result !== 'object') return result;
  const content = (result as { content?: Array<{ type?: string; text?: string }> }).content;
  if (!Array.isArray(content) || !content[0]?.text) return result;
  const text = content[0].text;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

export async function callMcpTool<T = unknown>(
  appId: string,
  mcpKey: string,
  toolName: string,
  args: Record<string, unknown> = {}
): Promise<McpToolCallResult<T>> {
  if (!appId?.trim()) throw new Error('app_id is required');
  if (!mcpKey?.trim()) throw new Error('MCP key is required — provision or rotate in the MCP Connection card');
  if (!toolName?.trim()) throw new Error('tool name is required');

  const res = await fetch(`/mcp/app/${encodeURIComponent(appId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${mcpKey}`,
    },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: Date.now(),
      method: 'tools/call',
      params: { name: toolName, arguments: args },
    }),
  });

  const text = await res.text().catch(() => '');
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = { error: text || res.statusText };
  }

  if (!res.ok) {
    const msg = payload?.error?.message || payload?.error || `${res.status} ${res.statusText}`;
    throw new Error(String(msg));
  }

  if (payload?.error) {
    throw new Error(payload.error.message || JSON.stringify(payload.error));
  }

  const result = payload?.result;
  if (result && typeof result === 'object' && (result as { isError?: boolean }).isError) {
    const parsed = parseToolContentText(result);
    const errMsg =
      parsed && typeof parsed === 'object' && 'error' in (parsed as object)
        ? String((parsed as { error: unknown }).error)
        : JSON.stringify(parsed);
    throw new Error(errMsg);
  }

  const data = parseToolContentText(result) as T;
  return { raw: result, data };
}
